import { Component, Input, HostBinding } from '@angular/core';

/**
 * Form Field Wrapper Component
 *
 * Automatically applies responsive column classes based on field type.
 * This centralizes the layout rules so they don't need to be repeated in every form.
 *
 * Column Rules:
 * - text: min 4 columns, max 6 columns (col-sm-12 col-md-4 col-lg-6)
 * - date: 2 columns (col-sm-12 col-md-2)
 * - number: 2 columns (col-sm-12 col-md-2)
 * - notes/textarea: min 6 columns, max 12 columns (col-sm-12 col-md-6 col-lg-12)
 * - dropdown: min 4 columns (col-sm-12 col-md-4)
 * - checkbox: 4 columns with flex alignment
 *
 * Usage:
 * <div class="row">
 *   <app-form-field type="text">
 *     <app-base-form-label [fieldNameInput]="'name'" ... />
 *     <app-base-form-input [type]="'text'" ... />
 *   </app-form-field>
 *
 *   <app-form-field type="dropdown">
 *     <app-base-form-label [fieldNameInput]="'category'" ... />
 *     <app-auto-complete ... />
 *   </app-form-field>
 *
 *   <app-form-field type="notes">
 *     <app-base-form-label [fieldNameInput]="'description'" ... />
 *     <app-form-textarea ... />
 *   </app-form-field>
 * </div>
 */
@Component({
  selector: 'app-form-field',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./form-field-wrapper.component.scss']
})
export class FormFieldWrapperComponent {

  /**
   * Field type determines the column width:
   * - 'text': Text input fields (min 4, max 6 columns)
   * - 'date': Date picker fields (2 columns)
   * - 'number': Number input fields (2 columns)
   * - 'notes': Textarea/notes fields (min 6, max 12 columns)
   * - 'dropdown': Dropdown/autocomplete fields (min 4 columns)
   * - 'checkbox': Checkbox fields with flex alignment (4 columns)
   * - 'full': Full width field (12 columns)
   */
  @Input() type: 'text' | 'date' | 'number' | 'notes' | 'dropdown' | 'checkbox' | 'full' = 'text';

  /**
   * Override the default column width for dropdown fields
   * Options: 4, 6, 8, 12
   */
  @Input() cols: 2 | 3 | 4 | 6 | 8 | 12 | null = null;

  @HostBinding('class') get fieldClasses(): string {
    const baseClasses = 'mb-3';

    // If custom cols specified, use that
    if (this.cols !== null) {
      if (this.type === 'checkbox') {
        return `${baseClasses} col-sm-12 col-md-${this.cols} d-flex align-items-center`;
      }
      return `${baseClasses} col-sm-12 col-md-${this.cols}`;
    }

    // Otherwise use type-based defaults
    switch (this.type) {
      case 'text':
        // Text: min 4 col, max 6 col
        return `${baseClasses} col-sm-12 col-md-4 col-lg-6`;

      case 'date':
      case 'number':
        // Date/Number: 2 columns
        return `${baseClasses} col-sm-12 col-md-2`;

      case 'notes':
        // Notes/Textarea: min 6 col, max 12 col
        return `${baseClasses} col-sm-12 col-md-6 col-lg-12`;

      case 'dropdown':
        // Dropdown: min 4 columns
        return `${baseClasses} col-sm-12 col-md-4`;

      case 'checkbox':
        // Checkbox: 4 columns with flex alignment
        return `${baseClasses} col-sm-12 col-md-4 d-flex align-items-center`;

      case 'full':
        // Full width
        return `${baseClasses} col-12`;

      default:
        return `${baseClasses} col-sm-12 col-md-4 col-lg-6`;
    }
  }
}
